import * as React from 'react';
import { styled } from '../theme-provider';
import { Breakpoints } from '../helpers/breakpoint';
import { getCommonProps } from '../helpers/common-props';
const BreakpointWrapperRenderingContext = React.createContext({});
export const BreakpointWrapperRenderingConfig = ({ notHydrating, ssrViewportWidth, children, }) => {
    const outerContextValue = React.useContext(BreakpointWrapperRenderingContext);
    const value = React.useMemo(() => ({
        notHydrating: notHydrating !== null && notHydrating !== void 0 ? notHydrating : outerContextValue.notHydrating,
        ssrViewportWidth: ssrViewportWidth !== null && ssrViewportWidth !== void 0 ? ssrViewportWidth : outerContextValue.ssrViewportWidth,
    }), [notHydrating, ssrViewportWidth, outerContextValue]);
    return (React.createElement(BreakpointWrapperRenderingContext.Provider, { value: value }, children));
};
const combinedMediaQuery = (props) => {
    const minQuery = `(min-width: ${Math.max(0, props.minSize)}px)`;
    const maxQuery = Number.isFinite(props.maxSize) && `(max-width: ${props.maxSize - 1}px)`;
    return [minQuery, maxQuery].filter(Boolean).join(' and ');
};
const StyledBreakpointWrapper = styled.div.withConfig({ displayName: "StyledBreakpointWrapper", componentId: "sc-1jaqtkh" }) `display:none;@media ${combinedMediaQuery}{display:contents;}`;
export function BreakpointWrapper(props) {
    const { min = Breakpoints.default, max = Breakpoints.never, children } = props;
    const commonProps = getCommonProps(props);
    const { ssrViewportWidth: contextSsrViewportWidth } = React.useContext(BreakpointWrapperRenderingContext);
    const ssrViewportWidth = contextSsrViewportWidth;
    const isRenderedInClient = useIsRenderedInClient();
    const { notHydrating } = React.useContext(BreakpointWrapperRenderingContext);
    const breakpointMatches = useBreakpointRangeReachedListener(min, max);
    const breakpointMatchesForSsr = isBreakpointRangeReached(min, max, ssrViewportWidth);
    // SSR
    if (!isRenderedInClient && !breakpointMatchesForSsr) {
        return null;
    }
    // client, but not hydrating, so act as if it was SSR
    if (isRenderedInClient && notHydrating && !breakpointMatchesForSsr) {
        return null;
    }
    // client, but hydrating
    if (isRenderedInClient && !notHydrating && !breakpointMatches) {
        return null;
    }
    return (React.createElement(StyledBreakpointWrapper, Object.assign({ minSize: min, maxSize: max }, commonProps), children));
}
function useMediaQueryListener(query) {
    const mql = React.useMemo(() => typeof window !== 'undefined' && query && window.matchMedia(query), [query]);
    const [matches, setMatches] = React.useState(mql ? mql.matches : false);
    React.useEffect(() => {
        if (!mql) {
            return;
        }
        setMatches(mql.matches);
        const handleMediaQueryListener = (event) => {
            setMatches(event.matches);
        };
        mql.addEventListener('change', handleMediaQueryListener);
        return () => {
            mql.removeEventListener('change', handleMediaQueryListener);
        };
    }, [mql]);
    return matches;
}
function isBreakpointRangeReached(minSize, maxSize, viewportWidth) {
    // if there is no given viewportWidth, we should always render the wrapped content
    // (it will be hidden by the css anyway)
    return viewportWidth !== undefined
        ? viewportWidth >= minSize && viewportWidth < maxSize
        : true;
}
function useBreakpointRangeReachedListener(minSize, maxSize) {
    const query = React.useMemo(() => combinedMediaQuery({ minSize, maxSize }), [minSize, maxSize]);
    return useMediaQueryListener(query);
}
function useIsRenderedInClient() {
    const [isRenderedInClient, setIsRenderedInClient] = React.useState(false);
    // useEffect only happens when we are in the browser not on the server
    // (except when testing with react-test-renderer)
    React.useEffect(() => {
        if (!isRenderedInClient) {
            // we need to check if we are really in the browser here
            // because of react-test-renderer
            setIsRenderedInClient(typeof window !== 'undefined');
        }
    }, [isRenderedInClient]);
    return isRenderedInClient;
}
